import Link from "next/link";
import { useParams, usePathname } from "next/navigation";
import { useCallback, useEffect, useMemo, useState } from "react";

import Fuse from "fuse.js";
import { RiHome3Line } from "react-icons/ri";

import { useLeftSideBarStore } from "~/state/store";
import { cn } from "~/utils/fontsConfig";

import { UserPermissions } from "~/server/api/helpers/permissionHelpers";
import { api } from "~/utils/api";
import BatchEventsModal from "../../../../app/_components/BatchEventsModal";
import ProposeEventModal from "../../modals/ProposeEventModal";
import SubNavigationGroup from "../../navigation/SubNavigationGroup";
import { Card } from "../../shadcn/Card";
import EditCommunityURLDialog from "./EditCommunityURLDialog";
import ManageCommunityBadgesDialog from "./ManageCommunityBadgesDialog";
import { getSideBarItems } from "./SidebarItems";
import { ModalType, SidebarItems } from "./SidebarTypes";

import { usePinnedItemsQuery } from "../_queries/usePinnedItems";
import CommunityStatusDropdown from "~/components/community-components/CommunityStatusDropdown";
import { useSession } from "next-auth/react";
import { If } from "../../Conditional";

const CommunityManagementBar = ({
    permissions,
}: {
    permissions: UserPermissions;
}) => {
    const { data: session } = useSession();
    const params = useParams<{ communitySlug: string; slug: string }>();
    const pathname = usePathname() ?? "";

    const { setSideBarState, sideBarState } = useLeftSideBarStore();

    const { communitySlug, slug } = params || {
        communitySlug: undefined,
        slug: undefined,
    };

    const { data: community } = api.community.getSimpleCommunityBySlug.useQuery(
        {
            communtiyslug: communitySlug as string,
        },
        {
            enabled: !!communitySlug,
        },
    );

    const { data: pinnedItems } = usePinnedItemsQuery(
        community?.id,
        community?.pinnedItems?.menuItems,
    );

    const [actualSlug, setActualSlug] = useState<string | undefined>();

    useEffect(() => {
        if (slug) {
            setActualSlug(slug as string);
        }

        if (communitySlug) {
            setActualSlug(communitySlug as string);
        }
    }, [communitySlug, slug]);

    const [openModalType, setOpenModalType] = useState<ModalType>();
    const updateModalType = useCallback((type: ModalType) => {
        setOpenModalType(type);
    }, []);

    const SIDEBAR_ITEMS = useMemo(
        () =>
            getSideBarItems(
                actualSlug as string,
                updateModalType,
                pinnedItems ?? [],
            ),
        [actualSlug, pinnedItems],
    );

    const [searchTerm, setSearchTerm] = useState("");

    const flattenedItems = useMemo(() => {
        return Object.values(SIDEBAR_ITEMS).flatMap((section) =>
            section.items.map((item) => ({ ...item, section })),
        );
    }, [SIDEBAR_ITEMS]);

    const fuse = useMemo(
        () =>
            new Fuse(flattenedItems, {
                keys: ["name", "section"],
                threshold: 0.4,
            }),
        [flattenedItems],
    );

    const filteredItems = useMemo(() => {
        if (!searchTerm) return SIDEBAR_ITEMS;

        const searchResults = fuse.search(searchTerm);
        const groupedResults = searchResults.reduce(
            (acc: SidebarItems, { item }) => {
                if (!acc[item.section.title]) {
                    acc[item.section.title] = {
                        title: item.section.title,
                        Icon: item.section.Icon,
                        items: [],
                    };
                }

                acc[item.section.title]?.items.push(item);

                return acc;
            },
            {},
        );

        return groupedResults;
    }, [searchTerm, SIDEBAR_ITEMS, fuse]);

    const isHome = pathname === `/${actualSlug}`;

    return (
        <>
            <>
                <span className="lg:overflow-none lg:dunset block h-screen overflow-y-auto lg:my-4 lg:ml-4 lg:h-auto">
                    <div className="flex w-full max-w-[300px] flex-col gap-4 bg-v3-dark-gray p-3 lg:rounded-[10px]">
                        {/* Home Card */}
                        <Card className="gap-1 rounded-lg bg-v3-card-lighter-gray p-4 px-2 py-2">
                            <div
                                className={cn(
                                    "grid grid-cols-[10%_90%] items-center justify-center rounded-lg px-4 py-2 hover:bg-v3-N-400",
                                    {
                                        "bg-v3-N-400 text-white": isHome,
                                    },
                                )}
                            >
                                <RiHome3Line className="text-lg" />
                                <Link href={`/${actualSlug}`}>Home</Link>
                            </div>
                        </Card>

                        <If condition={session?.user?.role === "ADMIN"}>
                            <Card className="gap-1 rounded-lg bg-v3-card-lighter-gray p-4 px-2 py-2">
                                <CommunityStatusDropdown
                                    communitySlug={communitySlug as string}
                                    disabled={session?.user?.role !== "ADMIN"}
                                />
                            </Card>
                        </If>

                        {Object.entries(filteredItems).map(([key, section]) => (
                            <SubNavigationGroup
                                key={key}
                                section={section}
                                community={community}
                            ></SubNavigationGroup>
                        ))}
                        <div className="block h-24 lg:hidden" />
                    </div>
                </span>

                <EditCommunityURLDialog
                    isOpen={openModalType === "slug"}
                    setIsOpen={() => setOpenModalType(undefined)}
                    communitySlug={communitySlug as string}
                />
                <ManageCommunityBadgesDialog
                    isOpen={openModalType === "badges"}
                    setIsOpen={() => setOpenModalType(undefined)}
                    communitySlug={communitySlug as string}
                />
                {community && (
                    <ProposeEventModal
                        community={community}
                        setOpen={(open: boolean) =>
                            setOpenModalType(open ? "new-event" : undefined)
                        }
                        open={openModalType === "new-event"}
                        hasManagingPermissions={
                            permissions.isManager || permissions.isAdmin
                        }
                    />
                )}
                <BatchEventsModal
                    isOpen={openModalType === "batch-events"}
                    setIsOpen={() => setOpenModalType(undefined)}
                    communitySlug={communitySlug as string}
                />
            </>
            <If condition={sideBarState !== "closed"}>
                <div
                    className="absolute left-[300px] top-0 z-10 h-full w-full"
                    onClick={() => setSideBarState("closed")}
                />
            </If>
        </>
    );
};

export default CommunityManagementBar;
